// ============================================================================
// MfaScreen — two-step login for HQ (superuser/admin).
//   step 'enroll'    → shows a QR code to scan with an authenticator app
//                      (Google Authenticator, Microsoft Authenticator, Authy…)
//   step 'challenge' → asks for the 6-digit code from that app
// After a correct code the session becomes "aal2" and HQ powers unlock.
// ============================================================================
import { useEffect, useState } from 'react';
import { supabase } from '../../supabase';
import { T } from '../../utils';
import { FontLink } from '../../components/UI';

export default function MfaScreen({ step, onVerified, onSignOut }) {
  const [factorId, setFactorId] = useState(null);
  const [qr, setQr] = useState(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      if (step === 'enroll') {
        // Remove any half-finished set-up, then start a fresh one
        const { data: list } = await supabase.auth.mfa.listFactors();
        for (const f of (list?.all || []).filter((x) => x.status !== 'verified')) {
          await supabase.auth.mfa.unenroll({ factorId: f.id });
        }
        const { data, error: err } = await supabase.auth.mfa.enroll({ factorType: 'totp', friendlyName: 'CCN HQ' });
        if (err) { setError(err.message); return; }
        setFactorId(data.id);
        setQr(data.totp.qr_code);
      } else {
        const { data } = await supabase.auth.mfa.listFactors();
        const factor = (data?.totp || []).find((f) => f.status === 'verified');
        if (factor) setFactorId(factor.id);
      }
    })();
  }, [step]);

  const verify = async () => {
    setBusy(true); setError('');
    const { error: err } = await supabase.auth.mfa.challengeAndVerify({ factorId, code: code.trim() });
    setBusy(false);
    if (err) setError('That code did not work. Check the app and try again.');
    else onVerified();
  };

  return (
    <div style={{ minHeight: '100vh', background: T.cream, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: "'DM Sans',sans-serif" }}>
      <FontLink />
      <div style={{ background: T.white, borderRadius: 16, padding: '36px 32px', maxWidth: 420, textAlign: 'center', boxShadow: '0 12px 40px rgba(0,0,0,0.08)' }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: T.navy, fontFamily: "'Playfair Display',serif", marginBottom: 10 }}>
          {step === 'enroll' ? 'Set up two-step login' : 'Enter your login code'}
        </div>
        <div style={{ fontSize: 13, color: T.grey600, lineHeight: 1.7, marginBottom: 18 }}>
          {step === 'enroll'
            ? 'HQ accounts need an extra code to protect members’ details. Scan this QR code with an authenticator app (e.g. Google Authenticator), then type the 6-digit code it shows.'
            : 'Open your authenticator app and type the 6-digit code for CCN.'}
        </div>
        {step === 'enroll' && qr && <img src={qr} alt="QR code" style={{ width: 200, height: 200, marginBottom: 16 }} />}
        <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" maxLength={6} placeholder="123456"
          style={{ width: 160, fontSize: 22, letterSpacing: 6, textAlign: 'center', padding: 10, borderRadius: 8, border: `1.5px solid ${T.grey200}`, marginBottom: 14 }} />
        {error && <div style={{ color: T.red, fontSize: 13, marginBottom: 12 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button onClick={verify} disabled={busy || !factorId || code.trim().length !== 6}
            style={{ padding: '10px 22px', borderRadius: 8, background: T.navy, color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, opacity: busy ? 0.6 : 1 }}>
            Verify
          </button>
          <button onClick={onSignOut}
            style={{ padding: '10px 22px', borderRadius: 8, background: T.white, color: T.grey600, border: `1px solid ${T.grey200}`, cursor: 'pointer' }}>
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
